import React from 'react';
import { Lock } from 'lucide-react';

// Emblemas SVG del plano del estacionamiento: monograma Sura, sello "G" (Gerencia), flecha de rampa y escaleras.
// Todo es vectorial, con colores planos (sin ids de gradiente) para poder repetirse en la pagina.

export const COLOR_LOGO = '#1D2C57';
const COLOR_NAVY = '#132752';

const PROPORCION_LOGO = 512 / 772;
const PROPORCION_GER = 56 / 40;

// "S" cuadrada y escalonada de Sura: cuerpo principal + dos bloques sueltos (arriba a la
// derecha y abajo a la izquierda), con las ranuras blancas del logo original.
export function MonogramaSura({ alto = 40, color = COLOR_LOGO, className = '', style }) {
  return (
    <svg
      className={className}
      style={style}
      width={Math.round(alto * PROPORCION_LOGO * 100) / 100}
      height={alto}
      viewBox="0 0 512 772"
      fill={color}
      aria-hidden="true"
      focusable="false"
    >
      <path d="M0 70Q0 0 70 0H195Q212 0 212 18V262Q212 285 236 285H440Q512 285 512 360V735Q512 772 475 772H330Q302 772 302 745V512Q302 485 275 485H70Q0 485 0 415Z" />
      <path d="M262 24Q262 0 286 0H452Q512 0 512 58V220Q512 245 488 245H286Q262 245 262 221Z" />
      <path d="M0 549Q0 525 24 525H238Q262 525 262 549V772H24Q0 772 0 748Z" />
    </svg>
  );
}

// Sello "G" (Gerencia): placa dorada con borde y letras navy, para los puestos de gerencia.
export function MarcaGer({ alto = 28, className = '', style }) {
  return (
    <svg
      className={className}
      style={style}
      width={Math.round(alto * PROPORCION_GER * 100) / 100}
      height={alto}
      viewBox="0 0 56 40"
      role="img"
      aria-label="Gerencia"
      focusable="false"
    >
      <rect x="1.5" y="1.5" width="53" height="37" rx="10" fill="#F6B21B" stroke="#B87A06" strokeWidth="2" />
      <rect x="4.5" y="4.5" width="47" height="14" rx="7" fill="#FFFFFF" opacity="0.28" />
      <text
        x="28"
        y="30"
        textAnchor="middle"
        fontFamily="Inter, system-ui, sans-serif"
        fontSize="29"
        fontWeight="800"
        letterSpacing="0"
        fill={COLOR_NAVY}
      >
        G
      </text>
    </svg>
  );
}

// Marca de "libre": cuadro con una P, como una senal de estacionamiento.
export function MarcaP({ size = 30, className = '', style }) {
  return (
    <svg
      className={className}
      style={style}
      width={size}
      height={size}
      viewBox="0 0 32 32"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="1" y="1" width="30" height="30" rx="8" fill="#5B4F8E" />
      <path
        d="M11 24V8.5h6.2a5 5 0 0 1 0 10H14.4"
        fill="none"
        stroke="#FFFFFF"
        strokeWidth="3.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Auto visto desde arriba, con el frente hacia arriba (rot = 180 lo da vuelta).
function AutoRampa({ x, y, rot = 0 }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${rot}) scale(1.25)`}>
      <rect x="-7" y="-13" width="14" height="26" rx="5" fill={COLOR_NAVY} />
      <rect x="-5" y="-8" width="10" height="6" rx="1.6" fill="#C3D5EE" />
      <rect x="-5" y="5" width="10" height="4" rx="1.4" fill="#C3D5EE" />
      <rect x="-8.6" y="-9" width="2.4" height="5" rx="1" fill={COLOR_NAVY} />
      <rect x="6.2" y="-9" width="2.4" height="5" rx="1" fill={COLOR_NAVY} />
      <rect x="-8.6" y="5" width="2.4" height="5" rx="1" fill={COLOR_NAVY} />
      <rect x="6.2" y="5" width="2.4" height="5" rx="1" fill={COLOR_NAVY} />
    </g>
  );
}

// Flecha de bloque vertical (cabeza + asta) que apunta hacia arriba o hacia abajo.
function FlechaCarril({ cx, y0, largo, sube }) {
  const hw = 15;
  const sw = 6;
  const cab = 20;
  const p = sube
    ? [[cx, y0], [cx + hw, y0 + cab], [cx + sw, y0 + cab], [cx + sw, y0 + largo], [cx - sw, y0 + largo], [cx - sw, y0 + cab], [cx - hw, y0 + cab]]
    : [[cx, y0 + largo], [cx + hw, y0 + largo - cab], [cx + sw, y0 + largo - cab], [cx + sw, y0], [cx - sw, y0], [cx - sw, y0 + largo - cab], [cx - hw, y0 + largo - cab]];
  return (
    <polygon
      points={p.map((q) => q.join(',')).join(' ')}
      fill="#FFFFFF"
      stroke={COLOR_NAVY}
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
  );
}

// Rampa: calzada de dos carriles (uno sube, otro baja) separados por la linea discontinua,
// con una flecha y un auto en cada carril. Los autos se mueven por su carril (ver estilos.js).
export function Rampa({ ancho = 96, alto = 150, className = '' }) {
  const m = ancho / 2;
  const largo = 46;
  return (
    <svg
      className={className}
      width={ancho}
      height={alto}
      viewBox={`0 0 ${ancho} ${alto}`}
      aria-hidden="true"
      focusable="false"
    >
      <rect x="1.5" y="1.5" width={ancho - 3} height={alto - 3} rx="16" fill="#C3D5EE" stroke={COLOR_NAVY} strokeWidth="1.8" />
      <line
        x1={m}
        y1="12"
        x2={m}
        y2={alto - 12}
        stroke="#FFFFFF"
        strokeWidth="2.4"
        strokeDasharray="6 6"
        strokeLinecap="round"
      />
      <FlechaCarril cx={m / 2} y0={12} largo={largo} sube />
      <FlechaCarril cx={m + m / 2} y0={alto - 12 - largo} largo={largo} sube={false} />
      <g className="est-auto-sube">
        <AutoRampa x={m / 2} y={alto - 48} />
      </g>
      <g className="est-auto-baja">
        <AutoRampa x={m + m / 2} y={48} rot={180} />
      </g>
    </svg>
  );
}

// Escaleras: bloque blanco con borde navy grueso, peldanos ascendentes y una persona subiendo.
export function Escalera({ ancho = 83, alto = 104, className = '' }) {
  const x0 = 13;
  const x1 = ancho - 13;
  const y1 = alto - 13;
  const peldanos = 5;
  const w = (x1 - x0) / peldanos;
  const h = 9.5;
  const puntos = [`${x0},${y1}`];
  for (let i = 0; i < peldanos; i += 1) {
    puntos.push(`${x0 + w * i},${y1 - h * (i + 1)}`);
    puntos.push(`${x0 + w * (i + 1)},${y1 - h * (i + 1)}`);
  }
  puntos.push(`${x1},${y1}`);
  // Persona de pie sobre el tercer peldano (px, py = pies).
  const px = x0 + w * 2.5;
  const py = y1 - h * 3;
  return (
    <svg
      className={className}
      width={ancho}
      height={alto}
      viewBox={`0 0 ${ancho} ${alto}`}
      role="img"
      aria-label="Escaleras"
    >
      <rect x="2.5" y="2.5" width={ancho - 5} height={alto - 5} rx="7" fill="#FFFFFF" stroke={COLOR_NAVY} strokeWidth="5" />
      <polygon points={puntos.join(' ')} fill={COLOR_NAVY} stroke={COLOR_NAVY} strokeWidth="1" strokeLinejoin="round" />
      <circle cx={px + 1} cy={py - 27} r="3.8" fill={COLOR_NAVY} />
      <g stroke={COLOR_NAVY} strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" fill="none">
        <path d={`M${px + 0.5} ${py - 21} L${px - 1} ${py - 10}`} />
        <path d={`M${px - 1} ${py - 10} L${px - 5} ${py - 1}`} />
        <path d={`M${px - 1} ${py - 10} L${px + 4} ${py - 5} L${px + 6} ${py - 9.5}`} />
        <path d={`M${px + 0.2} ${py - 18} L${px + 6} ${py - 14}`} />
        <path d={`M${px + 0.2} ${py - 18} L${px - 5} ${py - 13}`} />
      </g>
    </svg>
  );
}

// Glifo de un tipo de puesto (leyenda, panel de detalle, selector). `escala` ~ tamano del logo.
export function GlifoTipo({ tipo, escala = 30 }) {
  if (tipo === 'libre') return <MarcaP size={Math.round(escala * 0.8)} />;
  if (tipo === 'privado_sura') return <MonogramaSura alto={escala} />;
  if (tipo === 'gerencia') {
    return (
      <span className="est-glifo-doble">
        <MonogramaSura alto={Math.round(escala * 0.62)} />
        <MarcaGer alto={Math.round(escala * 0.5)} />
      </span>
    );
  }
  return <Lock size={Math.round(escala * 0.6)} color={COLOR_NAVY} strokeWidth={2.4} aria-hidden="true" />;
}
